/**
 * media.js — image compression + Supabase Storage helpers for the Content
 * (calendars + mockups) feature.
 *
 * Design goals (see the plan):
 *  - Keep the free-plan footprint tiny: every image is downscaled to display
 *    size and re-encoded to WebP before upload (a 3 MB photo → ~150 KB).
 *  - Reels are NEVER uploaded — only a captured cover frame (an image) is.
 *  - Deleting a calendar/mockup purges its Storage objects to reclaim space.
 *
 * Buckets (public):
 *   content-media       — cover/post/carousel images + brand logos
 *   published-mockups   — self-contained shareable mockup HTML (like reports)
 */

import { supabase } from './supabaseClient';

export const CONTENT_BUCKET = 'content-media';
export const MOCKUP_BUCKET  = 'published-mockups';

// ─── Public URL for a stored object ───────────────────────────────
export function publicUrl(bucket, path) {
  if (!path) return '';
  try {
    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    return data?.publicUrl || '';
  } catch {
    const base = process.env.REACT_APP_SUPABASE_URL || '';
    return `${base}/storage/v1/object/public/${bucket}/${path}`;
  }
}
export const mediaUrl = (path) => publicUrl(CONTENT_BUCKET, path);

// ─── Load a File/Blob into an <img> ───────────────────────────────
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.crossOrigin = 'anonymous';
    img.src = src;
  });
}

/**
 * Downscale + re-encode an image File/Blob to a compact WebP Blob.
 * @param {File|Blob} file
 * @param {{maxEdge?:number, quality?:number, mime?:string}} opts
 * @returns {Promise<Blob>}
 */
export async function compressImage(file, opts = {}) {
  const { maxEdge = 1080, quality = 0.82, mime = 'image/webp' } = opts;
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    let { width, height } = img;
    const longest = Math.max(width, height);
    if (longest > maxEdge) {
      const scale = maxEdge / longest;
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    const canvas = document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await new Promise((res) => canvas.toBlob(res, mime, quality));
    // Fallback: some browsers can't encode WebP — use JPEG.
    if (!blob) {
      return await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));
    }
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Capture a single frame from a video (File or direct URL) at time `t`
 * (seconds) and return it as a compact WebP Blob. Used for "pick cover
 * from the reel" — the video itself is never stored.
 * NOTE: works with local File objects, or direct/CORS-enabled video URLs.
 */
export function captureFrame(source, t = 0, opts = {}) {
  return new Promise((resolve, reject) => {
    const isFile = source instanceof Blob;
    const url = isFile ? URL.createObjectURL(source) : source;
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    const cleanup = () => { if (isFile) URL.revokeObjectURL(url); };
    video.onloadedmetadata = () => {
      const target = Math.min(Math.max(t, 0), (video.duration || 1) - 0.05);
      const seek = () => { try { video.currentTime = target; } catch { /* retry on canplay */ } };
      video.onseeked = async () => {
        try {
          const { maxEdge = 1080, quality = 0.82 } = opts;
          let w = video.videoWidth, h = video.videoHeight;
          const longest = Math.max(w, h);
          if (longest > maxEdge) { const s = maxEdge / longest; w = Math.round(w * s); h = Math.round(h * s); }
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(video, 0, 0, w, h);
          let blob = await new Promise((res) => canvas.toBlob(res, 'image/webp', quality));
          if (!blob) blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));
          cleanup();
          resolve(blob);
        } catch (e) { cleanup(); reject(e); }
      };
      if (video.readyState >= 2) seek();
      else video.oncanplay = seek;
    };
    video.onerror = () => { cleanup(); reject(new Error('Could not load video. For remote reels the link must be a direct, CORS-enabled video file — otherwise upload a designed cover instead.')); };
    video.src = url;
  });
}

// ─── Upload a Blob to Storage (upsert = stable, no orphans) ────────
export async function uploadBlob(bucket, path, blob, contentType) {
  const { error } = await supabase.storage.from(bucket).upload(path, blob, {
    contentType: contentType || blob.type || 'application/octet-stream',
    upsert: true,
  });
  if (error) throw error;
  return path;
}

/**
 * Compress an image File and upload it to content-media at `path`.
 * Returns the storage path (store this on the row/cell).
 */
export async function uploadImageFile(path, file, opts) {
  const blob = await compressImage(file, opts);
  await uploadBlob(CONTENT_BUCKET, path, blob, blob.type || 'image/webp');
  return path;
}

// Upload an already-produced blob (e.g. a captured frame) to content-media.
export async function uploadCoverBlob(path, blob) {
  await uploadBlob(CONTENT_BUCKET, path, blob, blob.type || 'image/webp');
  return path;
}

// ─── List / delete / usage ────────────────────────────────────────
async function listFolder(bucket, prefix) {
  const { data, error } = await supabase.storage.from(bucket).list(prefix, {
    limit: 1000, sortBy: { column: 'name', order: 'asc' },
  });
  if (error) { console.error('[media] list', prefix, error.message); return []; }
  return data || [];
}

/**
 * Recursively remove everything under a folder prefix (2 levels deep is
 * enough for our deterministic paths). Reclaims Storage space on delete.
 */
export async function removeByPrefix(bucket, prefix) {
  const entries = await listFolder(bucket, prefix);
  const files = [];
  for (const e of entries) {
    // Supabase marks folders with a null id / no metadata.
    if (e.id === null || (!e.metadata && !e.name.includes('.'))) {
      const sub = await listFolder(bucket, `${prefix}/${e.name}`);
      sub.forEach(f => files.push(`${prefix}/${e.name}/${f.name}`));
    } else {
      files.push(`${prefix}/${e.name}`);
    }
  }
  if (files.length) {
    const { error } = await supabase.storage.from(bucket).remove(files);
    if (error) console.error('[media] remove', error.message);
  }
  return files.length;
}

// Copy one stored object to a new path (used when duplicating a mockup).
export async function copyObject(bucket, from, to) {
  const { error } = await supabase.storage.from(bucket).copy(from, to);
  if (error) throw error;
  return to;
}

// List a client's reusable media library (content-media/<clientId>/lib).
export async function listLibrary(clientId) {
  const entries = await listFolder(CONTENT_BUCKET, `${clientId}/lib`);
  return entries
    .filter(e => e.name && e.name.includes('.'))
    .map(e => ({ path: `${clientId}/lib/${e.name}`, url: publicUrl(CONTENT_BUCKET, `${clientId}/lib/${e.name}`), size: e.metadata?.size || 0 }));
}

export async function removePaths(bucket, paths) {
  const list = (paths || []).filter(Boolean);
  if (!list.length) return;
  const { error } = await supabase.storage.from(bucket).remove(list);
  if (error) console.error('[media] removePaths', error.message);
}

// Sum sizes (bytes) of all objects under a prefix — for the storage meter.
export async function folderUsage(bucket, prefix = '') {
  let total = 0;
  const walk = async (p) => {
    const entries = await listFolder(bucket, p);
    for (const e of entries) {
      if (e.id === null || (!e.metadata && !e.name.includes('.'))) {
        await walk(p ? `${p}/${e.name}` : e.name);
      } else {
        total += e.metadata?.size || 0;
      }
    }
  };
  await walk(prefix);
  return total;
}

export function fmtBytes(n) {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), u.length - 1);
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
}
